import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { Stack, Input, Select, SelectableOption, Text } from '@moda/om';
import { COUNTRIES, FormErrors, STATES_OR_REGIONS } from '@moda/portal-stanchions';
import { UserApiMutationError } from '../../../../lib/createUserApiError';
import { RestAddressAttributes, useAddressAutocomplete } from '../../../../hooks/useAddress';
import { getAttributesFromPlace } from './getAttributesFromPlace';

import './ShippingAddressFormFields.scss';

export const COUNTRY_OPTIONS: SelectableOption[] = COUNTRIES.map(country => ({
  label: country.name,
  value: country.alpha2Code
}));

const getStateOrRegionOptions = (
  selectedCountryOrRegion: string | undefined
): SelectableOption[] | undefined =>
  selectedCountryOrRegion
    ? STATES_OR_REGIONS[selectedCountryOrRegion]?.map(({ name }) => ({
        label: name,
        value: name
      }))
    : undefined;

interface Props {
  version?: 'full' | 'compact';
  address: RestAddressAttributes;
  onChange: React.Dispatch<React.SetStateAction<RestAddressAttributes>>;
  mutationFieldErrors: UserApiMutationError | undefined;
  validationErrors: FormErrors;
}

export const ShippingAddressFormFields: React.FC<Props> = ({
  version = 'full',
  address,
  onChange,
  mutationFieldErrors,
  validationErrors
}) => {
  const addressRef = useRef<HTMLInputElement>(null);
  const { place, isFocused } = useAddressAutocomplete(addressRef, address.countryCode);

  const stateOrRegionOptions = useMemo(
    () => getStateOrRegionOptions(address.countryCode),
    [address.countryCode]
  );

  const handleChange = useCallback(
    (field: keyof RestAddressAttributes, value: string) => {
      onChange(attributes => ({
        ...attributes,
        [field]: value
      }));
    },
    [onChange]
  );

  const handleCountryChange = useCallback(
    (value: string) => {
      onChange(attributes => ({
        ...attributes,
        countryCode: value,
        state: STATES_OR_REGIONS[value] ? STATES_OR_REGIONS[value][0].name : ''
      }));
    },
    [onChange]
  );

  useEffect(() => {
    if (place) {
      onChange(attributes => ({
        ...attributes,
        ...getAttributesFromPlace(place)
      }));
    }
  }, [place, onChange]);

  return (
    <div className="ShippingAddressFormFields__fields">
      <Input label="Country or Region" name="countryCode">
        <Select
          idRef="CountryOrRegionDropdown"
          label=""
          onChange={handleCountryChange}
          options={COUNTRY_OPTIONS}
          value={address.countryCode}
        />
      </Input>

      {version === 'full' && (
        <div className="ShippingAddressFormFields__group">
          <Input
            className="ShippingAddressFormFields__field"
            error={validationErrors.firstName?.message || mutationFieldErrors?.['first_name']}
            label="First Name*"
            name="firstName"
            onChange={value => handleChange('firstName', value)}
            value={address.firstName}
            maxLength={50}
          />
          <Input
            className="ShippingAddressFormFields__field"
            error={validationErrors.lastName?.message}
            label="Last Name*"
            name="lastName"
            onChange={value => handleChange('lastName', value)}
            value={address.lastName}
            maxLength={50}
          />
        </div>
      )}

      <Input
        error={validationErrors.addressLine1?.message || mutationFieldErrors?.['address_line1']}
        label="Address*"
        name="addressLine1"
        onChange={value => handleChange('addressLine1', value)}
        onKeyDown={event => event.key === 'Enter' && isFocused && event.preventDefault()}
        ref={addressRef}
        value={address.addressLine1}
      />

      <Input
        error={validationErrors.addressLine2?.message || mutationFieldErrors?.['address_line2']}
        label="Address Line 2 (Floor, Apt #)"
        name="addressLine2"
        onChange={value => handleChange('addressLine2', value)}
        value={address.addressLine2}
      />

      <div className="ShippingAddressFormFields__group">
        <Input
          className="ShippingAddressFormFields__field"
          error={validationErrors.city?.message || mutationFieldErrors?.city}
          label="City*"
          name="city"
          onChange={value => handleChange('city', value)}
          value={address.city}
        />

        <Input
          className="ShippingAddressFormFields__field"
          error={validationErrors.state?.message || mutationFieldErrors?.state}
          label={`State or Region${address.countryCode === 'US' ? '*' : ''}`}
          name="state"
          onChange={value => handleChange('state', value)}
          value={address.state}
        >
          {stateOrRegionOptions && (
            <Select
              idRef="StateOrRegionDropdown"
              label=""
              onChange={value => handleChange('state', value)}
              options={stateOrRegionOptions}
              value={stateOrRegionOptions.find(state => state.label === address.state)?.value}
              allowAutoFill
            />
          )}
        </Input>

        <Input
          className="ShippingAddressFormFields__field"
          error={validationErrors.postalCode?.message || mutationFieldErrors?.['postal_code']}
          label={`Zip/Postal${address.countryCode === 'US' ? '*' : ''}`}
          name="postalCode"
          onChange={value => handleChange('postalCode', value)}
          value={address.postalCode}
        />
      </div>

      {version === 'full' && (
        <Stack space={1}>
          <Input
            error={validationErrors['phone']?.message || mutationFieldErrors?.phone}
            label={`Phone Number${address.countryCode === 'US' ? '' : '*'}`}
            type="tel"
            name="phone"
            onChange={value => handleChange('phone', value)}
            value={address.phone}
          />
          <Text color="cement">Only used in case of delivery issues</Text>
        </Stack>
      )}
    </div>
  );
};
